import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Scales } from "@phosphor-icons/react";
import api from "../api";

// v415.3 — PARENT SPREAD CARD (distributor portal). Shows a parent like Raju
// the spread his subs have earned him: pending payout, weekly cut, per-sub
// breakdown and the latest sales. Hidden entirely when there is no spread
// history, so leaf distributors never see an empty card.
export const ParentSpreadCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/margin/my-spread").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const hasAny = data.pending_total > 0 || data.settled_total > 0 || (data.recent || []).length > 0;
  if (!hasAny) return null;

  const money = (v) => `$${Number(v || 0).toFixed(2)}`;

  return (
    <div className="border-2 border-[#c9a24a] rounded-xl p-4 sm:p-5 bg-[#fbf6e7]/40"
      data-testid="parent-spread-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
            <Scales size={18} weight="bold" className="text-[#c9a24a]" />
            {t("spread_card.title")}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-md">{t("spread_card.sub")}</p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500">
            {t("spread_card.pending")}
          </div>
          <div className="text-2xl font-bold text-[#8a6a1f]" data-testid="spread-pending-total">
            {money(data.pending_total)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <span className="text-[11px] px-2 py-1 rounded-full bg-white border border-zinc-200">
          {t("spread_card.this_week")}: <b>{money(data.week_spread)}</b>
        </span>
        <span className="text-[11px] px-2 py-1 rounded-full bg-white border border-zinc-200">
          {t("spread_card.last_week")}: <b>{money(data.prev_week_spread)}</b>
        </span>
        <span className="text-[11px] px-2 py-1 rounded-full bg-white border border-zinc-200">
          {t("spread_card.settled")}: <b>{money(data.settled_total)}</b>
        </span>
      </div>

      {(data.subs || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">
            {t("spread_card.per_sub")}
          </div>
          <div className="space-y-1.5">
            {data.subs.map((s) => (
              <div key={s.sub_id}
                className="flex items-center justify-between gap-2 bg-white border border-zinc-200 rounded-lg px-3 py-2"
                data-testid={`spread-sub-${s.sub_id}`}>
                <span className="text-sm font-medium">{s.sub_name}</span>
                <span className="text-xs text-zinc-500">
                  {t("spread_card.sales", { n: s.sale_count })}
                </span>
                <span className="text-sm font-bold text-[#8a6a1f]">{money(s.pending)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.recent || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">
            {t("spread_card.recent")}
          </div>
          <div className="space-y-1">
            {data.recent.slice(0, 5).map((m, i) => (
              <div key={i} className="flex items-baseline justify-between gap-2 text-[12px]"
                data-testid="spread-recent-row">
                <span className="truncate">
                  <span className="font-mono text-[10px] text-zinc-400 mr-1.5">
                    {(m.created_at || "").slice(0, 10)}
                  </span>
                  {m.quantity} × {m.product_name}
                  <span className="text-zinc-400"> · {m.sub_name}</span>
                </span>
                <span className="shrink-0">
                  <b className="text-[#8a6a1f]">{money(m.margin)}</b>
                  <span className={`ml-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${m.settled
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {m.settled ? t("spread_card.settled_badge") : t("spread_card.pending_badge")}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
